import PageHead from "../../../layout/PageHead";
import {
    EyeOutlined,
    EditOutlined,
    CloseOutlined,
    CheckOutlined,
    PlusOutlined,
    UsergroupAddOutlined
} from "@ant-design/icons";
import { ROUTES } from "../../../../config/consts/routes";
import { SECTIONS } from "../../../../config/constants";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import GroupCodesService from "../../../../services/groupcodes/GroupCodesService";
import TablesService from "../../../../services/tables/TablesService";
import { Tabs, Form, Drawer, Space, Button, Input, InputNumber, Checkbox, Select, Empty } from "antd";
import Datatable from "../../../shared/components/Datatable"
import ViewsService from "../../../../services/excel-addin/ViewsService";
import { UpdateViewRequest } from "../../../../models/ExcelAddin/UpdateViewRequest";
import { CreateViewRequest } from "../../../../models/ExcelAddin/CreateViewRequest";
import styles from "./ViewManagement.module.css";
import { UpdateField } from "../../../../models/ExcelAddin/UpdateField";
import UsersService from "../../../../services/admin/UsersService";
import { useSelector } from "react-redux";
import notificationUtil from "../../../shared/utils/NotificationsUtil";
import { NOTIFICATION_TYPES } from "../../../../config/constants";
import { Permission } from "../../../../models/ExcelAddin/Permission";
import RolesService from "../../../../services/excel-addin/RolesService";
import { GroupCode } from "../../../../models/groupcodes/GroupCode";
import { Entity } from "../../../../models/entities/Entity";
import {ViewType} from "../../../../models/views/ViewType";

const { TabPane } = Tabs;

enum RowAction {
    View = 1,
    Edit,
    Create,
}

const routes = [
    {
        path: ROUTES.general.main.route,
        breadcrumbName: SECTIONS.general.name,
    },
    {
        breadcrumbName: SECTIONS.excelAddin.name,
    },
    {
        path: ROUTES.excelAddin.viewManagement.route,
        breadcrumbName: ROUTES.excelAddin.viewManagement.name,
    },
];

const ViewManagement = () => {
    const { t, i18n } = useTranslation("translations");

    const [roles, setRoles] = useState<Role[]>([]);

    //pageHead
    const [groupCodes, setGroupCodes] = useState<GroupCode[]>([]);
    const [entities, setEntities] = useState<Entity[]>([]);
    const [operations, setOperations] = useState<string[]>([]);
    const [viewTypes, setViewTypes] = useState<ViewType[]>([]);

    const [selectedGroupCode, setSelectedGroupCode] = useState<number>();
    const [selectedEntity, setSelectedEntity] = useState<number>();
    const [selectedViewType, setSelectedViewType] = useState<number>();
    const [selectedOperation, setSelectedOperation] = useState<number>();

    //datatable
    const [data, setData] = useState<View[]>([]);
    const [isTableLoading, setIsTableLoading] = useState(true);

    const [editedRow, setEditedRow] = useState<View>();

    //drawer
    const [drawerAction, setDrawerAction] = useState<RowAction>();
    const [drawerTitle, setDrawerTitle] = useState<string>("");
    const [openDrawer, setOpenDrawer] = useState(false);
    const placement = "bottom";

    //form
    const [loadingButton, setLoadingButton] = useState(false);
    const [isValidForm, setIsValidForm] = useState(false);
    const [fields, setFields] = useState<Field[]>([]);
    const [isLoadingFields, setIsLoadingFields] = useState(false);
    const [form] = Form.useForm();
    const [editedFields, setEditedFields] = useState<UpdateField[]>([]);
    const [roleInput, setRoleInput] = useState<RoleView[]>([]);
    const [selectedRole, setSelectedRole] = useState<Role>();
    const [selectedFilterRoleId, setSelectedFilterRoleId] = useState<number>();
    const [deletedRoleIds, setDeletedRoleIds] = useState<number[]>();

    //permissions
    const [editAllowed, setEditAllowed] = useState<boolean>(false);

    const { profile } = useSelector((state: any) => ({
        profile: state.profile,
    }));

    interface View {
        id: number;
        name: string;
        tableName: string;
        tableId: number;
        postProcessing: string;
        defaultPermission: string;
        allowRowCreateDelete: boolean;
        entityId: number;
        entityName: string;
        viewTypeId: number;
        viewTypeName: string;
        roles: RoleView[];
    }

    interface Field {
        id: number;
        column_Name: string;
        permission: string;
    }

    interface RoleView {
        id: number,
        viewId: number,
        description: string,
        filter: string
    }

    interface Role {
        id: number,
        description: string
    }

    useEffect(() => {
        TablesService.getAllEntities().then((response) => {
            const data = response.data;
            setEntities(data);
        });
        
        GroupCodesService.getAll().then((response) => {
            const data = response.data;
            setGroupCodes(data);
        });

        ViewsService.getAllViewTypes().then((response) => {
            const data = response.data;
            setViewTypes(data);
        });

        TablesService.getAllOperations().then((response) => {
            const data = response.data;
            setOperations(data);
        });

        RolesService.getAll().then((response) => {
            const data = response.data;
            setRoles(data);
        });
    }, []);


    useEffect(() => {
        UsersService.permissions(profile.id)
            .then((response) => {
                setEditAllowed(response.data.isAdmin);
            })
    }, [profile])

    useEffect(() => {
        if (selectedEntity) {
            fetchViews();
        }
    }, [selectedEntity]);

    const fetchFields = (viewId) => {
        setIsLoadingFields(true);
        ViewsService.getFields(viewId)
            .then(res => {
                setFields(res.data);
            })
            .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }))
            .finally(() => setIsLoadingFields(false));
    };

    const fetchViews = () => {
        setIsTableLoading(true);
        if (selectedEntity) {
            handleDataFetch(ViewsService.getByEnitityId(selectedEntity));
        } else {
            handleDataFetch(ViewsService.getAll());
        }
    }

    const handleDataFetch = (fetchFunction) => {
        fetchFunction
            .then((response) => setData(response.data))
            .catch(error => notificationUtil.openNotificationDialog({
                description: error.response.data,
                type: NOTIFICATION_TYPES.ERROR,
                logError: false
            }))
            .finally(() => setIsTableLoading(false));
    };

    const onDoubleClickRow = (record: View) => {
        showDrawer(RowAction.View);
        setForm(record);
        setEditedRow(record);
        fetchFields(record.id);
    };

    const onAddRoleClick = () => {
        if (selectedRole) {
            const roleView: RoleView = {
                id: selectedRole.id,
                viewId: editedRow?.id,
                description: selectedRole.description,
                filter: ""
            }
            setRoleInput([...roleInput, roleView]);
            setDeletedRoleIds(deletedRoleIds.filter(id => id !== selectedRole.id));
            setSelectedRole(null);
        }
        onFieldsChange();
    }

    const onDeleteRole = (id: number) => {
        setRoleInput(roleInput.filter(r => r.id !== id));
        setDeletedRoleIds([...deletedRoleIds, id]);
        onFieldsChange();
    }

    const setForm = (view: View) => {
        form.setFieldsValue({
            ...view
        });
        setRoleInput(view.roles);
    }

    const showDrawer = (action: RowAction) => {
        setDrawerAction(action);
        switch (action) {
            case RowAction.View:
                setDrawerTitle("View details");
                break;
            case RowAction.Create:
                form.resetFields();
                setDrawerTitle("New view");
                break;
            case RowAction.Edit:
                setDrawerTitle("Edit view");
                break;
            default:
                break;
        }
        setOpenDrawer(true);
    };

    const onCloseDrawer = () => {
        setOpenDrawer(false);
        clearForm();
    };

    const clearForm = () => {
        form.resetFields();
        setEditedRow(null);
        setEditedFields([]);
        setSelectedRole(null);
        setRoleInput([]);
        setIsValidForm(false);
        setFields([]);
        setSelectedFilterRoleId(null);
        setDeletedRoleIds([]);
    }

    const onFieldsChange = () => {
        setIsValidForm(form.getFieldsError().filter(({ errors }) => errors.length).length === 0);
    }

    const onSaveView = () => {
        setLoadingButton(true);

        const newView: CreateViewRequest = form.getFieldsValue();

        if (editedRow?.id) {

            const updatedRoles = roleInput.map(r => ({ roleId: r.id, filter: r.filter }));

            const viewToUpdate: UpdateViewRequest = {
                ...newView,
                updatedFields: editedFields,
                UpdatedRoles: updatedRoles,
                deletedRoleIds: deletedRoleIds
            }

            ViewsService.update(editedRow.id, viewToUpdate)
                .then(() => {
                    fetchViews();
                    onCloseDrawer();
                    notificationUtil.openNotificationDialog({ description: "View edited successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
                })
                .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }))
                .finally(() => {
                    setLoadingButton(false);
                });
        } else {
            ViewsService.create(newView)
                .then(() => {
                    fetchViews();
                    onCloseDrawer();
                    notificationUtil.openNotificationDialog({ description: "View created successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
                })
                .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }))
                .finally(() => {
                    setLoadingButton(false);
                });
        }
    }

    const onDeleteView = (id: number) => {
        ViewsService.remove(id)
            .then(() => {
                fetchViews();
                notificationUtil.openNotificationDialog({ description: "View deleted successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
            })
            .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }));
    };

    const onEditView = (record: View) => {
        showDrawer(RowAction.Edit);
        setForm(record);
        setEditedRow(record);
        fetchFields(record.id);
    };

    const onNewViewButtonClick = () => {
        showDrawer(RowAction.Create);
    };

    const onChangePermission = (fieldId: number, fieldName: string ,value: Permission) => {
        const updatedFields = fields.map((field) => {
            if (fieldId ? field.id === fieldId : field.column_Name === fieldName) {
                return {
                    ...field,
                    permission: value
                }
            }
            return field;
        });
        setFields(updatedFields);

        //check if fiels is already in editedFields
        const field = fieldId ? editedFields.find(field => field.id === fieldId) : editedFields.find(field => field.fieldName === fieldName);

        if (field) {
            const updatedEditedFields = editedFields.map((field) => {
                if (field.id === fieldId) {
                    return {
                        ...field,
                        value: value
                    }
                }
                return field;
            });
            setEditedFields(updatedEditedFields);
        } else {
            var updatedFieldRequest: UpdateField = { id: fieldId, fieldName: fieldName, value: value };
            setEditedFields([...editedFields, updatedFieldRequest]);
        }
        onFieldsChange();
    }

    const onEditFilter = (record: RoleView) => {
        setSelectedFilterRoleId(record.id);
    }

    const onFilterChange = (value: string) => {
        setRoleInput(roleInput.map(u => {
            if (u.id === selectedFilterRoleId) {
                return {
                    ...u,
                    filter: value
                }
            }
            return u;
        }));
        onFieldsChange();
    }


    const viewActionButton = (
        <Button
            type={"primary"}
            onClick={onNewViewButtonClick}
            className="actionButton"
            hidden={!editAllowed}
        >
            <PlusOutlined />
            NEW VIEW
        </Button>
    );

    const scroll = { y: "calc(100vh - 320px)" };

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            filters: [],
            filterMode: "tree"
        },
        {
            title: "Table Path",
            dataIndex: "tableName",
            key: "tableName",
            filters: [],
            filterMode: "tree"
        },
        {
            title: "Table Id",
            dataIndex: "tableId",
            key: "tableId",
            filters: [],
            filterMode: "tree"
        },
        {
            title: "Entity",
            dataIndex: "entityName",
            key: "entityName",
            filters: [],
            filterMode: "tree"
        },
        {
            title: "View Type",
            dataIndex: "viewTypeName",
            key: "viewTypeName",
            filters: [],
            filterMode: "tree"
        },
        {
            title: "Allow create rows",
            dataIndex: "allowRowCreateDelete",
            key: "allowRowCreateDelete",
            filters: [],
            filterMode: "tree"
        },
        {
            title: "Post Processing",
            dataIndex: "postProcessing",
            key: "postProcessing",
            filters: [],
            filterMode: "tree"
        },
        {
            title: "Default Permission",
            dataIndex: "defaultPermission",
            key: "defaultPermission",
            filters: [],
            filterMode: "tree"
        },
    ]

    const fieldsColumns = [
        {
            title: "Name",
            dataIndex: "column_Name",
            key: "column_Name"
        },
        {
            title: "Permission",
            dataIndex: "permission",
            key: "permission",
            render(value: Permission, record: Field) {
                return <Select value={value} onChange={value => {
                    onChangePermission(record.id, record.column_Name, value);
                }}>
                    <Select.Option value="edit">Update</Select.Option>
                    <Select.Option value="view">Read</Select.Option>
                    <Select.Option value="hidden">Hidden</Select.Option>
                    <Select.Option value="default">Use default</Select.Option>
                </Select>
            }
        }
    ];

    const ViewRoleColumns = [
        {
            title: "Role",
            dataIndex: "description",
            key: "description"
        },
        {
            title: "Filter",
            dataIndex: "filter",
            key: "filter",
            render(value, record) {
                if (selectedFilterRoleId === record.id) {
                    return <Input value={value} onChange={(e) => onFilterChange(e.target.value)} />
                } else {
                    return value;
                }
            }
        }
    ]

    return (
        <div>
            <PageHead
                title={t("Page.ExcelAddin.Headers.ViewManagement", {
                    lngs: i18n.languages,
                    defaultValue: "View Management",
                })}
                icon={<EyeOutlined />}
                routes={routes}
                groupCodes={groupCodes}
                entities={entities}
                operations={operations}
                selectedGroupCode={selectedGroupCode}
                selectedEntity={selectedEntity}
                selectedOperation={selectedOperation}
                setSelectedGroupCode={setSelectedGroupCode}
                setSelectedEntity={setSelectedEntity}
                setSelectedOperation={setSelectedOperation}
                setSelectGroupCodeDisabled={true}
                setSelectEntityDisabled={false}
                setSelectOperationDisabled={true}
                mainAction={viewActionButton}
                otherActions={[]}
                setActionsDisabled={false}
            />
            <div className="card-container">
                <Tabs
                    type="card"
                    className={styles.fullWidth}
                >
                    <TabPane
                        tab={t("Page.ExcelAddin.Headers.ViewManagement", {
                            lngs: i18n.languages,
                            defaultValue: "View management",
                        })}
                        key="ViewManagement"
                    >
                        {!selectedEntity ? (
                            <Empty
                                style={{ display: "flex", width: "100%" }}
                                description={t(
                                    "Page.Administration.ExcelAddIn.Tabs.ViewManagement.Tutorial.GetStarted",
                                    {
                                        lngs: i18n.languages,
                                        defaultValue:
                                            "Please select an Entity to get started",
                                    }
                                )}
                            ></Empty>
                        ) : (
                            <Datatable
                                scroll={scroll}
                                tableColumns={columns}
                                data={data}
                                setData={setData}
                                isTableLoading={isTableLoading}
                                editRowEnabled={editAllowed}
                                deleteRowEnabled={editAllowed}
                                onEditFunc={onEditView}
                                onDeleteFunc={onDeleteView}
                                onDoubleClickRow={onDoubleClickRow}
                                showActionsColumn={editAllowed}
                                paginationDisabled={true}
                            />
                        )}

                        <Drawer
                            width={"400px"}
                            getContainer={false}
                            className={styles.drawer}
                            title={drawerTitle}
                            placement={placement}
                            onClose={onCloseDrawer}
                            visible={openDrawer}
                            closable={false}
                            height={"75%"}
                            extra={
                                <Space>
                                    {RowAction?.View === drawerAction && (
                                        <>
                                            <Button
                                                onClick={() => showDrawer(RowAction.Edit)}
                                                icon={<EditOutlined />}
                                                className={"active-btn"}
                                                size="small"
                                                disabled={!editAllowed}
                                                hidden={!editAllowed}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                type="ghost"
                                                onClick={onCloseDrawer}
                                                icon={<CloseOutlined />}
                                                size="small"
                                            />
                                        </>
                                    )}
                                    {RowAction?.Create === drawerAction && (
                                        <>
                                            <Button
                                                onClick={onSaveView}
                                                icon={<CheckOutlined />}
                                                size="small"
                                                loading={loadingButton}
                                                disabled={!isValidForm}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                type="ghost"
                                                onClick={onCloseDrawer}
                                                icon={<CloseOutlined />}
                                                size="small"
                                            />
                                        </>
                                    )}
                                    {RowAction?.Edit === drawerAction && (
                                        <>
                                            <Button
                                                onClick={onSaveView}
                                                icon={<CheckOutlined />}
                                                size="small"
                                                loading={loadingButton}
                                                disabled={!isValidForm}
                                            >
                                                Update
                                            </Button>
                                            <Button
                                                type="ghost"
                                                onClick={onCloseDrawer}
                                                icon={<CloseOutlined />}
                                                size="small"
                                            />
                                        </>
                                    )}
                                </Space>
                            }
                        >
                            <Form form={form} onFieldsChange={onFieldsChange} disabled={RowAction.View === drawerAction ? true : false} layout="vertical">
                                <div className={styles.columnsWrap}>
                                    <div className={styles.leftColumn}>
                                        <Form.Item label="Name:"
                                                   name="name"
                                                   rules={[
                                                       {
                                                           required: true,
                                                       },
                                                   ]}
                                        >
                                            <Input
                                                allowClear
                                                placeholder="enter value"
                                            ></Input>
                                        </Form.Item>
                                        <Form.Item label="Table path:" name="tableName">
                                            <Input
                                                allowClear
                                                placeholder="enter value"
                                            ></Input>
                                        </Form.Item>
                                        <Form.Item label="Table id:" name="tableId">
                                            <InputNumber
                                                placeholder="enter value"
                                            ></InputNumber>
                                        </Form.Item>
                                        <Form.Item label="Entity:" name="entityId"
                                                   rules={[
                                                       {
                                                           required: true,
                                                           message: 'Please select an entity',
                                                       },
                                                   ]}>
                                            <Select
                                                showSearch={true}
                                                showArrow={true}
                                                allowClear={true}
                                                placeholder={t("General.Placeholders.EnterValue", {
                                                    lngs: i18n.languages,
                                                    defaultValue: "Enter value",
                                                })!}
                                                value={selectedEntity}
                                                onChange={setSelectedEntity}
                                                options={entities.map((entity) => ({
                                                    label: entity.name,
                                                    value: entity.id,
                                                }))}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                filterSort={(optionA, optionB) =>
                                                    (optionA!.label as unknown as string)
                                                        .toLowerCase()
                                                        .localeCompare(
                                                            (optionB!.label as unknown as string).toLowerCase()
                                                        )
                                                }
                                            />
                                        </Form.Item>
                                        <Form.Item label="Post processing:" name="postProcessing">
                                            <Input
                                                allowClear
                                                placeholder="enter value"
                                            ></Input>
                                        </Form.Item>
                                        <Form.Item label="Default permission:" name="defaultPermission"
                                                   rules={[
                                                       {
                                                           required: true,
                                                           message: 'Please select an default permission',
                                                       },
                                                   ]}>
                                            <Select placeholder="Select a default permission" allowClear={false}>
                                                <Select.Option value="edit">edit</Select.Option>
                                                <Select.Option value="view">view</Select.Option>
                                                <Select.Option value="hidden">hidden</Select.Option>
                                            </Select>
                                        </Form.Item>
                                        <div>
                                            <Form.Item label="View type:" name="viewTypeId"
                                                       rules={[
                                                           {
                                                               required: true,
                                                               message: 'Please select a view type',
                                                           },
                                                       ]}>
                                                <Select
                                                    showSearch={true}
                                                    showArrow={true}
                                                    allowClear={true}
                                                    placeholder={t("General.Placeholders.SelectOption", {
                                                        lngs: i18n.languages,
                                                        defaultValue: "Select option",
                                                    })!}
                                                    value={selectedViewType}
                                                    onChange={setSelectedViewType}
                                                    options={viewTypes.map((viewType) => ({
                                                        label: viewType.description,
                                                        value: viewType.id,
                                                    }))}
                                                    filterOption={(input, option) =>
                                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                    }
                                                    filterSort={(optionA, optionB) =>
                                                        (optionA!.label as unknown as string)
                                                            .toLowerCase()
                                                            .localeCompare(
                                                                (optionB!.label as unknown as string).toLowerCase()
                                                            )
                                                    }
                                                />
                                            </Form.Item>
                                            <Form.Item label="Allow create rows:" name="allowRowCreateDelete" valuePropName="checked">
                                                <Checkbox />
                                            </Form.Item>
                                        </div>
                                    </div>
                                    {RowAction?.Create !== drawerAction && (
                                        <div className={styles.middleColumn}>
                                            <Datatable
                                                scroll={scroll}
                                                tableColumns={fieldsColumns}
                                                showActionsColumn={false}
                                                data={fields}
                                                setData={setFields}
                                                isTableLoading={isLoadingFields}
                                            />
                                        </div>
                                    )}
                                    {RowAction?.Create !== drawerAction && (
                                        <div className={styles.rightColumn}>

                                            <Form.Item label="Add Role:">
                                                <div
                                                    className={styles.addRoleSelectWrap}
                                                >
                                                    <Select
                                                        allowClear
                                                        showSearch
                                                        className={styles.fullWidth}
                                                        placeholder={t("General.Placeholders.SelectOption", {
                                                            lngs: i18n.languages,
                                                            defaultValue: "Select option",
                                                        })}
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={
                                                            roles.filter(r => {
                                                                return !roleInput.map(r => r.id).includes(r.id)
                                                            })
                                                                .map(r => ({ value: r.id, label: r.description }))
                                                        }
                                                        value={selectedRole?.id}
                                                        onChange={(roleId) => {
                                                            const role = roles.find(r => r.id === roleId);
                                                            setSelectedRole(role);
                                                        }}
                                                    />
                                                    <Button
                                                        icon={<UsergroupAddOutlined />}
                                                        type="primary"
                                                        className={styles.green_btn}
                                                        onClick={() => onAddRoleClick()}
                                                    />
                                                </div>
                                            </Form.Item>

                                            <Datatable
                                                scroll={scroll}
                                                tableColumns={ViewRoleColumns}
                                                data={roleInput}
                                                setData={setRoleInput}
                                                editRowEnabled={editAllowed && RowAction?.Edit === drawerAction}
                                                deleteRowEnabled={editAllowed}
                                                showActionsColumn={editAllowed}
                                                isTableLoading={isTableLoading}
                                                paginationPageSize={10}
                                                onEditFunc={onEditFilter}
                                                onDeleteFunc={onDeleteRole}
                                            />
                                        </div>
                                    )}
                                </div>

                            </Form>
                        </Drawer>
                    </TabPane>
                </Tabs>
            </div>
        </div>
    );
}

export { ViewManagement };